'use client';

import React, { useCallback, useEffect, useMemo, useRef, useTransition } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { useTranslations } from 'use-intl';
import IntermediaryProductList from '@/components/organisms/product-list/components/intermediary-page';
import ProductListingV2 from '@/components/pages/product-listing-v2';
import { ProductListingProductEntry, ProductListingState } from '@/components/pages/product-listing-v2/types';
import { FilterPillSpec } from '@/components/organisms/filter-toolbar/types';
import { QuickFilterPillSpec } from '@/components/molecules/quick-filter-pills/types';
import { FilterDrawerSection, StagedFilters } from '@/components/organisms/all-filters-drawer-v2/types';
import { PurchaseList as DrawerPurchaseList } from '@/components/organisms/add-to-purchase-list-drawer/types';
import { ProductTileV2Role } from '@/components/molecules/product-tile-v2/types';
import { SearchPageHeaderProps } from '@/components/molecules/search-page-header/types';
import { rememberQueryId } from '@/lib/algolia/query-id-store';
import { mapCategory } from '@/utils/mappers/map-category';
import { mapAlgoliaProduct } from '@/utils/mappers/map-algolia-product';
import { mapPurchaseList } from '@/utils/mappers/map-purchase-list';
import { resolveReference } from '@/utils/lib/resolve-reference';
import { isProfaneQuery } from '@/utils/profanity';
import { buildPlpBreadcrumb } from '@/utils/build-plp-breadcrumb';
import useCart from '@/lib/hooks/useCart';
import useAccount from '@/lib/hooks/useAccount';
import useAccountRoles from '@/lib/hooks/useAccountRoles';
import useShoppingListAccess from '@/lib/hooks/useShoppingListAccess';
import usePurchaseLists from '@/lib/hooks/usePurchaseLists';
import useCustomRouter from '@/hooks/useCustomRouter';
import { trackClick, trackClickedFilters, trackConvert, trackView, type AlgoliaProduct } from '@/lib/algolia';
import {
  pushSearchNoResults,
  pushSearchRefined,
  pushSearchResultAddToCart,
  pushSearchResultClick,
  pushSearchResultsViewed,
  pushViewItemList,
  pushSelectItem,
  pushAddToCart,
  pushAddToList,
  type GA4Item,
  type SearchFilters,
  type SearchUserType,
} from '@/lib/analytics/dataLayer';
import { algoliaHitToGA4Item } from '@/lib/analytics/ga4Items';
import useCurrency from '@/hooks/useCurrency';
import useImpressionTracker from '@/hooks/useImpressionTracker';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import toast from '@/components/atoms/toaster/helpers/toast';
import { CategoryFilter } from '@shared/types/product/CategoryFilter';
import { normalizeBarcode } from '@/lib/algolia/barcode';
import { STANDALONE_FILTER_PARAMS } from '@/lib/algolia/filterable-attributes';
import { DataSourceProps, ViewModelProps } from '../../types';
import useFilterRefinement from '../../hooks/useFilterRefinement';
import { addToPurchaseListWithFeedback } from './helpers/addToPurchaseListWithFeedback';
import { collectImmediateChildCategoryNames, formatFilterLabel } from './helpers/categoryFilter';
import { buildSubcategoryNav, type SubcategoryLink } from './helpers/subcategoryNav';
import { resolveListingState } from './helpers/resolveListingState';
import type { Locale } from '@/project.config';
import type { CtProductSnapshot } from '@/utils/server/getCtSnapshotForSku';

// EWM-185 product-list view model.
//
// Two render branches sharing the same data context:
//   1. displayIntermediaryPage = true   -> IntermediaryView
//      (root category landing UX with category cards + highlights;
//       gated by useIntermediaryCategoryPage + descendants >= 3 in
//       the server tastic)
//   2. otherwise                        -> V2View
//      (the v2 PLP composition: FilterToolbar + tiles + pagination
//       + SeoContent + AddToPurchaseListDrawer)
//
// Each branch is its own component so React's rules-of-hooks isn't
// violated by an early return between hook calls.
//
// PLP data is fully server-rendered. Algolia hits arrive synchronously
// via `initialAlgoliaData`; CT price enrichment is awaited server-side
// (in the tastic, via `getCtSnapshotForSku`) and arrives here as
// resolved `ctData`. Sort / filter / pagination all push a new URL,
// the server tastic re-runs, and fresh server-resolved data flows back
// down — no client-side Algolia fetch.
type Shared = Omit<DataSourceProps, 'category'> & ViewModelProps;

const IntermediaryView = ({ category, categoryConfiguration }: Shared) => {
  const translate = useTranslations();
  const { slug, locale } = useParams();
  const searchParams = useSearchParams();
  const isSearchPage = !!searchParams.get('query');

  const currency = useCurrency();

  const rootCategory = category ? mapCategory(category, { locale }) : undefined;
  const categoryConfig = categoryConfiguration[category?.slug ?? ''] ?? {};

  const breadcrumb = isSearchPage
    ? [
        { name: translate('common.home'), link: '/' },
        { name: translate('product.search-results'), link: '' },
      ]
    : [
        { name: translate('common.home'), link: '/' },
        ...((slug as string[]) ?? []).map((chunk, i, arr) => ({
          name: chunk.replace(/[-_]/g, ' '),
          link: `/${arr.slice(0, i + 1).join('/')}`,
        })),
      ];

  return (
    <IntermediaryProductList
      title={rootCategory?.name ?? ''}
      link={{ name: translate('common.shop-all'), href: `${category?._url}?view=1` }}
      breadcrumb={breadcrumb}
      items={[
        ...(rootCategory?.descendants ?? []).map(({ name, path, categoryKey }) => ({
          name: name ?? '',
          image: categoryConfiguration[categoryKey as keyof typeof categoryConfiguration]?.image,
          url: path,
        })),
        {
          name: translate('common.view-all'),
          image: categoryConfig?.image,
          url: `${category?._url}?view=1`,
        },
      ]}
      highlight={{
        headline: categoryConfig.highlightHeadline,
        subline: categoryConfig.highlightSubline,
        cta: resolveReference(categoryConfig.highlightCtaReference, categoryConfig.highlightCta),
        items: (categoryConfig.highlightItems ?? []).map(({ name, image, price, reference, pressTargetPosition }) => ({
          name,
          image,
          price,
          currency,
          url: resolveReference(reference).href ?? '#',
          pressTargetPosition: pressTargetPosition ?? 'bottom',
        })),
      }}
    />
  );
};

// Algolia facet attributes that back the inline pills + drawer accordions.
// Single source of truth so the URL refinement keys match the facet IDs
// across SSR + UI. EWM-185 / EWM-240 (Luke 2026-05-28): only Brand and
// Category are required inline filters; everything else lives in the
// dynamic per-category drawer driven by `category.categoryFilter` JSON
// (EWM-185 / EWM-240 dynamic-drawer work, this file).
const BRAND_FACET_ATTR = 'brand';
const CATEGORY_FACET_ATTR = 'categories.name';

/**
 * Every STATICALLY-declared facet attribute the filter drawer can send, on the
 * TERM channel. Exported so `quickFilterBindings.spec.tsx` can assert the drawer
 * agrees with the SSR allowlist (EWM-1074 review B3).
 *
 * The drift guard used to cover `QUICK_PILL_BINDINGS` alone, and the review's
 * point was that the pills are not where a facet is most likely to be added —
 * a static drawer section is. Such a section would ship a control that ticks,
 * renders active and filters nothing, which is the defect this ticket exists to
 * remove.
 *
 * `drawerSections` is built inside a hook from runtime data, so a spec cannot
 * read it. The two static sections are built FROM this array, so neither can
 * drift from the allowlist.
 *
 * Be precise about what that does and does not buy, because an earlier version
 * of this comment overstated it: a THIRD static section added inline with a
 * literal `id` still bypasses both the array and the spec — measured, all tests
 * stay green. The guard ties the sections that exist; it does not make a new
 * unguarded one unexpressible. Closing that needs the array to be the only way
 * to push a static section, which is EWM-1084.
 *
 * Per-category `additionalAttribute.*` sections are deliberately absent: they
 * are page-scoped dynamic facets, allowed by the dynamic set the page requested
 * rather than by this static list.
 */
export const STATIC_DRAWER_FACET_ATTRIBUTES = [BRAND_FACET_ATTR, CATEGORY_FACET_ATTR] as const;

// EWM-185 / EWM-240 — Algolia path prefix for the dynamic per-category
// filter attributes. Each `categoryFilter.classifyingcategory` value is
// the bare key inside `additionalAttribute` (e.g. `Safety_Rating_Cat`)
// — the FE prepends this prefix so the Algolia facet query and URL
// state key match `additionalAttribute.<classifyingcategory>`.
// Probe-confirmed (2026-06-01) that `searchable(additionalAttribute)`
// in `attributesForFaceting` propagates to every nested leaf, so no
// per-attribute Algolia config is required.
const DYNAMIC_FACET_ATTR_PREFIX = 'additionalAttribute.';
const dynamicFacetAttr = (classifyingcategory: string): string => `${DYNAMIC_FACET_ATTR_PREFIX}${classifyingcategory}`;

// Quick-filter pill mapping. Each entry binds a UI pill to one of three
// kinds of refinements:
//   - 'boolean':  facet boolean attribute on the URL (`facets[<attr>][boolean]=T`).
//   - 'term':     specific value on a term-faceted attribute. Toggling on adds
//                 the value to the term list; toggling off removes it.
//   - 'url-flag': a plain non-facet URL param (e.g. `?showDiscontinued=T`)
//                 read directly by the tastic — server-side switch outside
//                 the facet machinery.
//
// EWM-185 / EWM-240 (Luke 2026-05-28 + Dan v0 mock @ /tmp/v0-extract/
// src/app/{category,search}/page.tsx): the category PLP toolbar shows
// *only* Brand and Category inline filters — no In Stock / New Product /
// Special Order pills. Dan's mock declares a `quickPills` array on the
// category page (line ~103) but never renders it in the toolbar JSX;
// the search page mock has no pill row at all. Anything beyond Brand +
// Category lives in the per-category dynamic drawer (separate follow-up
// once Luke confirms the CT field key + JSON shape).
//
// EWM-686 — "Show in-stock only" (enabled 2026-09-29 on PO direction, Leo Ryan,
// Jira comment 78171). One boolean binding on the Algolia record attribute
// `inStock` (true = in stock), delivered by EWM-1039 and `filterOnly(inStock)`
// on all six {dev,uat,prod}_products_sd{au,nz} indices. Ticking it writes
// `facets[inStock][boolean]=T`; SSR applies it only because `inStock` is also in
// `BOOLEAN_FACET_ATTRIBUTES` (`lib/algolia/filterable-attributes.ts`, EWM-1074).
// Add or remove the two together — `quickFilterBindings.spec.tsx` fails if only
// one moves. It is in the SHARED array (not `SEARCH_…`) because category PLP,
// /search and collections (EWM-660) all run this view-model.
//
// KNOWN DATA GAPS, accepted by the PO (measured 2026-09-29, exhaustive counts
// over the visible `NOT lifeCycleAttribute:"GO"` catalogue):
//   * dev/QA (QA reads the `dev_*` indices): 611 of 9,411 AU and 3,853 of 7,307
//     NZ records carry no `inStock`, so a ticked filter excludes them whatever
//     their real stock is. uat NZ has 2 such records.
//   * prod (measured 2026-09-29 09:50 +08:00): `inStock` is now populated — AU
//     9,268 true / 1,256 false / 135 no value; NZ 6,425 / 1,973 / 47 no value.
//     Those 135 AU / 47 NZ records are excluded when the filter is ticked, the
//     same class as the dev/QA gaps above. Re-run `/conformity-check` Step 3
//     (`algolia.record.in-stock-attr-discriminates`) before promoting to prod.
//
// Why not `isAvailable`, which PR #1137 bound: it is `false` iff
// `lifeCycleAttribute` is "GO", and `build-filters.ts` already excludes GO from
// every query (EWM-643), so it could never narrow a listing (QA-failed
// 2026-09-16). Mind the spelling too: Algolia answers a filter on a misspelt or
// non-facetable attribute with HTTP 200 and 0 hits, so a typo here would empty
// every listing silently. And do NOT add `inStock` to `ALGOLIA_PLP_FACETS`: it
// is `filterOnly`, and requesting it as a facet returns `facets: {}` silently.
//
// MIND THE NAME. `inStock` is ALSO this repo's own `Product` field
// (`types/entity/product.ts`), fed from commercetools (`getCtSnapshotForSku`
// maps `stock.isOnStock` -> `inStock`); the tile badge is CT-fed (EWM-966) and
// `map-algolia-product.ts` hardcodes `inStock: false`. `AlgoliaProduct` does not
// declare `inStock`, so the Algolia attribute can never reach the badge. Here
// `inStock` means the Algolia record attribute ONLY — do not conflate the two.
// The filter and the badge can therefore disagree for a product whose index
// value is missing or stale (AC-7, accepted; convergence is EWM-1086).
//
// History (why the binding was removed in PR #1191 and restored here) lives in
// `docs/superpowers/tickets/EWM-686/dossier.md`.
type QuickPillBinding = {
  id: string;
  i18nKey: string;
} & (
  | { kind: 'boolean'; attribute: string }
  | { kind: 'term'; attribute: string; value: string }
  | { kind: 'url-flag'; param: string; value: string }
);
const IN_STOCK_FACET_ATTR = 'inStock';
export const QUICK_PILL_BINDINGS: QuickPillBinding[] = [
  { id: IN_STOCK_FACET_ATTR, i18nKey: 'product.show-in-stock-only', kind: 'boolean', attribute: IN_STOCK_FACET_ATTR },
];

// EWM-185 / EWM-240 (Luke 2026-05-28 + Dan v0 mock @ /tmp/v0-extract/
// src/app/search/page.tsx): the /search toolbar also drops the
// Show-Discontinued pill — Dan's search mock has no pill row, and Luke
// scoped the inline toolbar to Brand + Category only. The server-side
// `?showDiscontinued=T` reader in lib/tastics/product-list/index.tsx
// stays as a QA / ops affordance (Z-line items can still be surfaced
// by typing the param into the URL); only the UI toggle goes.
const SEARCH_QUICK_PILL_BINDINGS: QuickPillBinding[] = [];

// Parse selected refinement values for a given facet from the existing
// useFilterRefinement URL shape: `facets[<id>][terms][N]=<value>`.
const parseSelectedTerms = (searchParams: URLSearchParams, facetId: string): string[] => {
  const prefix = `facets[${facetId}][terms]`;
  return Array.from(searchParams.entries())
    .filter(([k]) => k.startsWith(prefix))
    .map(([, v]) => v)
    .filter((v): v is string => !!v);
};

// Parse a single boolean facet from the useFilterRefinement URL shape
// `facets[<id>][boolean]=T`. Absent param = unset = false.
const parseSelectedBoolean = (searchParams: URLSearchParams, facetId: string): boolean =>
  searchParams.get(`facets[${facetId}][boolean]`) === 'T';

// EWM-240: plain non-facet URL flag (e.g. `?showDiscontinued=T`).
// Active when the URL param exactly matches the binding's value.
const parseSelectedUrlFlag = (searchParams: URLSearchParams, param: string, value: string): boolean =>
  searchParams.get(param) === value;

// All the V2 PLP rendering props the renderer needs but that don't
// change between the pending (no-prices) and resolved (with-prices)
// branches of the Suspense boundary. Extracted into a shape so the
// outer `V2View` computes them once and hands them to both branches.
interface V2RenderProps {
  title: string;
  // EWM-312 — short category description rendered as the H1 sub-line on
  // PLP. Suppressed on /search (no category).
  description?: string;
  // EWM-189 — optional editorial/SEO rich-text below the grid, sourced
  // from the category's `seoContent` custom field. Undefined on /search
  // (no category) and on categories without content, so the band hides.
  seoHtml?: string;
  seoTitle?: string;
  breadcrumb?: Array<{ label: string; href?: string }>;
  // EWM-832 — immediate child categories that contain products, listed as text
  // links between the H1 and the filter row. Undefined on /search and
  // collection pages, and for a category with no qualifying children, so the
  // page omits the block entirely rather than leaving a gap.
  subcategories?: SubcategoryLink[];
  searchHeader?: SearchPageHeaderProps;
  role: ProductTileV2Role;
  // EWM-653 — forwarded to each tile (Master-only purchase-list access).
  canAddToPurchaseList?: boolean;
  // EWM-360 — BU `back-orders-allowed`; enables a "Back Order" CTA on OOS tiles.
  backOrdersAllowed: boolean;
  totalResults: number;
  state: ProductListingState;
  errorType?: 'network' | 'validation';
  query?: string;
  quickFilterPills: QuickFilterPillSpec[];
  filterPills: FilterPillSpec[];
  drawerSections: FilterDrawerSection[];
  totalActiveFilters: number;
  currentPage: number;
  totalPages: number;
  // EWM-372: true while a filter refinement's SSR re-run is in flight; drives
  // the grid loading treatment.
  isRefining: boolean;
  purchaseLists: Array<{ id: string; name: string; itemCount: number }>;
  sortOptions: Array<{ value: string; label: string }>;
  sortValue: string;
  viewMode: 'grid' | 'list';
  onPillChange: (id: string, value: string[]) => void;
  // EWM-374: drawer commits all staged selections in one call on Apply.
  onApplyFilters: (staged: StagedFilters) => void;
  onClearFilters: () => void;
  onPageChange: (page: number) => void;
  onSortChange: (value: string) => void;
  onViewModeChange: (mode: 'grid' | 'list') => void;
  onProductClick: (productId: string, position: number) => void;
  onTileVisible: (objectID: string, node: HTMLElement | null) => void;
  onAddToCart: (productId: string, qty: number) => Promise<void>;
  onAddToPurchaseList: (productId: string, selectedListIds: string[]) => Promise<void>;
  // EWM-601 — create a purchase list inline from the AddToPurchaseListDrawer.
  onCreatePurchaseList: (name: string) => Promise<DrawerPurchaseList | null>;
  onQuickFilterToggle: (id: string, next: boolean) => void;
  onRetry: () => void;
}

// Pure renderer — takes a hit list + per-SKU CT promises, maps hits to
// product entries (shell only, no CT yet), hands to `ProductListingV2`.
// The tile component awaits its own promise under a Suspense boundary
// and overrides price + stock + Add-to-Cart state when CT resolves.
interface PlpRendererProps extends V2RenderProps {
  hits: AlgoliaProduct[];
  ctPromisesBySku?: Record<string, Promise<CtProductSnapshot | null>>;
}
const PlpRenderer = ({ hits, ctPromisesBySku, ...shell }: PlpRendererProps) => {
  // EWM-643 — the removed-from-sale (`GO`) exclusion is applied by Algolia via
  // the `NOT lifeCycleAttribute:"GO"` clause in buildAlgoliaFilters, NOT here.
  // A post-fetch pass was considered and rejected: it would read the same field
  // off the same hit, so it cannot catch index lag (a stale record carries no
  // GO code to match), and dropping hits after the fact desynchronises the
  // grid from Algolia's hit count, pagination and facet counts.
  const productEntries: ProductListingProductEntry[] = useMemo(() => hits.map(mapAlgoliaProduct), [hits]);
  return <ProductListingV2 {...shell} products={productEntries} ctPromisesBySku={ctPromisesBySku} />;
};

const V2View = ({
  category,
  categories,
  initialAlgoliaData,
  algoliaErrorType,
  noProductSelection,
  categoryOutOfSelection,
  ctPromisesBySku,
  collection,
}: Shared) => {
  const translate = useTranslations();
  // EWM-832 — `locale` drives the child-category hrefs (slugs are authored
  // en-AU only, so NZ needs the resolveLocalizedUrl fallback chain).
  const { slug, locale } = useParams();
  const searchParams = useSearchParams();
  const router = useCustomRouter();
  const searchQuery = searchParams.get('query');
  // EWM-238: `isSearchRoute` is sticky to the /search URL — true even when
  // the user has cleared the query input, so the SearchPageHeader stays
  // mounted. `isSearchPage` keeps its existing meaning (a query is set)
  // and continues to drive Algolia analytics + empty-state branching.
  const slugArr = Array.isArray(slug) ? (slug as string[]) : slug ? [slug as string] : [];
  const isSearchRoute = slugArr.at(-1) === 'search';
  const isSearchPage = !!searchQuery;
  // EWM-660: collection mode — a plain title header (no breadcrumb, no search
  // bar) above the standard facet toolbar + grid. Driven by the `collection`
  // prop from the /collections/[handle] route.
  const isCollection = !!collection;

  // EWM-403 — profane query: suppress reflecting the term back in the no-results header, so the page shows a generic "No results found".
  const queryBlocked = isSearchPage && isProfaneQuery(searchQuery);

  const listId = isCollection ? 'plp_collection' : isSearchRoute ? 'plp_search' : `plp_${category?.slug ?? 'all'}`;
  const listName = collection?.title ?? (isSearchRoute ? 'Search Results' : (category?.name ?? 'Product List'));

  const { account } = useAccount();
  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits();
  const { permissions } = useAccountRoles(selectedBusinessUnit?.key);
  const { canUsePurchaseLists } = useShoppingListAccess(selectedBusinessUnit?.key);
  const { addItem } = useCart(selectedBusinessUnit?.key, selectedStore?.key);
  const { purchaseLists: rawWishlists, addToWishlists, createPurchaseList } = usePurchaseLists(selectedStore?.key);
  const {
    setTermFacet,
    setBooleanFacet,
    setUrlFlag,
    clearAllFacets,
    applyFacets,
    setSort,
    currentSort,
    setView,
    currentView,
    setPage,
    currentPage,
  } = useFilterRefinement();

  // EWM-372 — wrap filter refinements (pill / quick-toggle / drawer Apply /
  // clear) in a transition so the SSR re-run is non-blocking and `isRefining`
  // stays true until the new results commit. The renderer dims the grid +
  // shows a spinner while it's pending, giving immediate feedback for the
  // otherwise-silent round-trip. Separate from `isLoadingMore` (append) so the
  // two loaders don't fight.
  const [isRefining, startRefine] = useTransition();

  // Server-resolved Algolia state. All sync, no client fetching.
  const hits: AlgoliaProduct[] = initialAlgoliaData?.hits ?? [];
  const nbHits = initialAlgoliaData?.nbHits ?? 0;
  const nbPages = initialAlgoliaData?.nbPages ?? 0;

  const algoliaFacets = initialAlgoliaData?.facets ?? {};
  const queryID = initialAlgoliaData?.queryID;
  const algoliaIndexName = initialAlgoliaData?.indexName ?? '';

  // Read current refinements off the URL. Reads, not writes — writes go
  // through useFilterRefinement so pagination resets on filter change.
  const selectedBrands = useMemo(
    () => parseSelectedTerms(new URLSearchParams(searchParams.toString()), BRAND_FACET_ATTR),
    [searchParams],
  );
  const selectedCategories = useMemo(
    () => parseSelectedTerms(new URLSearchParams(searchParams.toString()), CATEGORY_FACET_ATTR),
    [searchParams],
  );

  // Algolia Insights impressions. Collect objectIDs as the user dwells
  // on tiles, then flush in a single `viewedObjectIDs` event so we don't
  // spam the Insights endpoint on long scrolls. Algolia's batched event
  // payload supports up to 20 objectIDs; we cap at 20 per flush and roll
  // the rest into the next tick.
  const impressionQueueRef = useRef<string[]>([]);
  const impressionFlushTimerRef = useRef<number | null>(null);
  const flushImpressions = useCallback(() => {
    impressionFlushTimerRef.current = null;
    const queued = impressionQueueRef.current;
    if (queued.length === 0) return;
    const batch = queued.slice(0, 20);
    impressionQueueRef.current = queued.slice(20);
    trackView({
      index: algoliaIndexName,
      objectIDs: batch,
      eventName: isSearchPage ? 'Hits viewed (search)' : 'Hits viewed (PLP)',
    });
    if (impressionQueueRef.current.length > 0) {
      // `flushImpressions` re-schedules itself via setTimeout inside its own useCallback body, so the
      // identifier is used before its `const` is declared and there is no position to hoist it to. The
      // recursion is ASYNCHRONOUS: each tick runs the closure captured when the timer was scheduled, so
      // if `algoliaIndexName`/`isSearchPage` change while the queue is still draining, the remaining
      // batches report the values captured at schedule time. That stale-attribution window is
      // pre-existing (this diff only added the comment) and benign for impression analytics — bounded by
      // the 250ms drain cadence — so we keep the self-reschedule and suppress the rule rather than
      // restructure behavior in a lint-conformance sweep. Logged per EWM-760's P-disable fallback.
      // eslint-disable-next-line react-hooks/immutability -- EWM-760: self-recursive callback, no hoist position (see above).
      impressionFlushTimerRef.current = window.setTimeout(flushImpressions, 250);
    }
  }, [algoliaIndexName, isSearchPage]);

  const enqueueImpression = useCallback(
    (objectID: string) => {
      impressionQueueRef.current.push(objectID);
      if (impressionFlushTimerRef.current != null) return;
      impressionFlushTimerRef.current = window.setTimeout(flushImpressions, 250);
    },
    [flushImpressions],
  );

  const ga4ImpressionQueueRef = useRef<GA4Item[]>([]);
  const ga4FlushTimerRef = useRef<number | null>(null);

  const flushGA4Impressions = useCallback(() => {
    ga4FlushTimerRef.current = null;
    const batch = ga4ImpressionQueueRef.current.splice(0);
    if (batch.length === 0) return;
    pushViewItemList({ item_list_id: listId, item_list_name: listName, items: batch });
  }, [listId, listName]);

  const enqueueGA4Impression = useCallback(
    (objectID: string) => {
      const hit = hits.find((h) => h.objectID === objectID);
      if (!hit) return;
      const index = hits.indexOf(hit) + 1;
      ga4ImpressionQueueRef.current.push(algoliaHitToGA4Item(hit, index, listId, listName));
      if (ga4ImpressionQueueRef.current.length >= 12) {
        if (ga4FlushTimerRef.current != null) {
          window.clearTimeout(ga4FlushTimerRef.current);
          ga4FlushTimerRef.current = null;
        }
        flushGA4Impressions();
      } else if (ga4FlushTimerRef.current == null) {
        ga4FlushTimerRef.current = window.setTimeout(flushGA4Impressions, 250);
      }
    },
    [hits, listId, listName, flushGA4Impressions],
  );

  // Reset GA4 impression queue on queryID change so stale items from a
  // previous search context aren't flushed into the new one.
  useEffect(() => {
    ga4ImpressionQueueRef.current = [];
    if (ga4FlushTimerRef.current != null) {
      window.clearTimeout(ga4FlushTimerRef.current);
      ga4FlushTimerRef.current = null;
    }
  }, [queryID]);

  const handleTileImpression = useCallback(
    (objectID: string) => {
      enqueueImpression(objectID);
      enqueueGA4Impression(objectID);
    },
    [enqueueImpression, enqueueGA4Impression],
  );

  const { observe: observeTile } = useImpressionTracker<string>({
    // Each new server-issued queryID resets the tracker's fired set so
    // re-visited tiles can fire again under the new search context.
    resetKey: queryID,
    onImpression: handleTileImpression,
  });

  // Flush any pending queue on unmount so impressions that landed in
  // the last <250ms aren't dropped.
  useEffect(() => {
    return () => {
      if (impressionFlushTimerRef.current != null) {
        window.clearTimeout(impressionFlushTimerRef.current);
        impressionFlushTimerRef.current = null;
      }
      if (impressionQueueRef.current.length > 0) {
        trackView({
          index: algoliaIndexName,
          objectIDs: impressionQueueRef.current.slice(0, 20),
          eventName: isSearchPage ? 'Hits viewed (search)' : 'Hits viewed (PLP)',
        });
        impressionQueueRef.current = [];
      }
      // GA4: flush any remaining items
      if (ga4FlushTimerRef.current != null) {
        window.clearTimeout(ga4FlushTimerRef.current);
        ga4FlushTimerRef.current = null;
      }
      if (ga4ImpressionQueueRef.current.length > 0) {
        pushViewItemList({
          item_list_id: listId,
          item_list_name: listName,
          items: ga4ImpressionQueueRef.current.splice(0),
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map Algolia facet counts → pill / drawer option specs. Sorted by
  // count desc so the highest-volume values surface first.
  const brandOptions = useMemo(() => {
    const counts = algoliaFacets[BRAND_FACET_ATTR] ?? {};
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .map(([value, count]) => ({ value, label: `${value} (${count})` }));
  }, [algoliaFacets]);

  // EWM-185 / EWM-240 — Category facet. `categories.name` is faceting-
  // enabled in the index (probe-confirmed); a product carries every
  // category in its hierarchy, so on Search this is the cross-result-set
  // category list and on a category PLP it narrows within the current
  // branch.
  //
  // EWM-595 — on a category PLP that hierarchy means the facet lists a flat
  // soup of every level: the current ("main") category, its ancestors, AND
  // deeper descendants. Show ONLY the current category's immediate
  // sub-categories so overlapping parent/grandchild pairs (e.g. "Resistors"
  // next to its grandchild "Wire-wound") collapse to one clean level. On
  // /search there's no current category, so `null` = show the full list.
  // EWM-832 — the subcategory navigation block. Deliberately fed by the
  // UNREFINED category facet (`unrefinedCategoryFacets`), not `algoliaFacets`:
  // the AC requires the same children to be listed regardless of the grid's
  // filters or search term, and the main query's facet counts shrink as
  // refinements are applied.
  const subcategories = useMemo(() => {
    if (isSearchRoute || isCollection) return undefined;
    return buildSubcategoryNav({
      category,
      allCategories: categories,
      categoryFacetCounts: initialAlgoliaData?.unrefinedCategoryFacets?.[CATEGORY_FACET_ATTR],
      locale: locale as Locale,
    });
  }, [isSearchRoute, isCollection, category, categories, initialAlgoliaData?.unrefinedCategoryFacets, locale]);

  const allowedCategoryNames = useMemo(
    () => (isSearchRoute ? null : collectImmediateChildCategoryNames(category, categories)),
    [isSearchRoute, category, categories],
  );
  const categoryOptions = useMemo(() => {
    const counts = algoliaFacets[CATEGORY_FACET_ATTR] ?? {};
    return Object.entries(counts)
      .filter(([value]) => allowedCategoryNames === null || allowedCategoryNames.has(value))
      .sort(([, a], [, b]) => b - a)
      .map(([value, count]) => ({ value, label: `${value} (${count})` }));
  }, [algoliaFacets, allowedCategoryNames]);

  const filterPills: FilterPillSpec[] = useMemo(() => {
    const pills: FilterPillSpec[] = [];
    if (brandOptions.length > 0 || selectedBrands.length > 0) {
      pills.push({
        id: BRAND_FACET_ATTR,
        label: translate('product.brand'),
        options: brandOptions,
        value: selectedBrands,
      });
    }
    if (categoryOptions.length > 0 || selectedCategories.length > 0) {
      pills.push({
        id: CATEGORY_FACET_ATTR,
        label: translate('product.categories-id'),
        options: categoryOptions,
        value: selectedCategories,
      });
    }
    return pills;
  }, [brandOptions, selectedBrands, categoryOptions, selectedCategories, translate]);

  const quickFilterPills: QuickFilterPillSpec[] = useMemo(() => {
    const url = new URLSearchParams(searchParams.toString());
    // EWM-240: Show-Discontinued pill appears on /search only — Z-line
    // default-hide is a Search business rule (EWM-239), and the toggle
    // doesn't make sense on category PLP browse.
    const bindings = isSearchRoute ? [...QUICK_PILL_BINDINGS, ...SEARCH_QUICK_PILL_BINDINGS] : QUICK_PILL_BINDINGS;
    return bindings.map((binding) => {
      let active: boolean;
      if (binding.kind === 'boolean') active = parseSelectedBoolean(url, binding.attribute);
      else if (binding.kind === 'term') active = parseSelectedTerms(url, binding.attribute).includes(binding.value);
      else active = parseSelectedUrlFlag(url, binding.param, binding.value);
      return { id: binding.id, label: translate(binding.i18nKey), active };
    });
  }, [searchParams, translate, isSearchRoute]);

  // EWM-185 / EWM-240 — dynamic per-category filter list driving the
  // additional drawer accordion sections after Brand + Category.
  //
  // PLP: `category.categoryFilter` (the current category's authored list).
  // /search: union across every category appearing in the result set,
  //   deduped by `classifyingcategory` (the first occurrence wins so
  //   `isFeatured` from any category is preserved into the union).
  //
  // Sort: `isFeatured` first (Luke confirmed 2026-06-01) so the
  // most-important filters render at the top of the drawer.
  const dynamicCategoryFilters = useMemo(() => {
    const source = !isSearchRoute
      ? (category?.categoryFilter ?? [])
      : (() => {
          const hitCategoryKeys = new Set(
            hits.flatMap((hit) => hit.categories?.map((c) => c.key).filter((k): k is string => !!k) ?? []),
          );
          const seen = new Map<string, CategoryFilter>();
          for (const cat of categories) {
            if (!cat.categoryKey || !hitCategoryKeys.has(cat.categoryKey)) continue;
            for (const filter of cat.categoryFilter ?? []) {
              if (!seen.has(filter.classifyingcategory)) seen.set(filter.classifyingcategory, filter);
            }
          }
          return Array.from(seen.values());
        })();
    return [...source.filter((f) => f.isFeatured), ...source.filter((f) => !f.isFeatured)];
  }, [isSearchRoute, category?.categoryFilter, hits, categories]);

  // Resolve current URL selections for every dynamic facet so the
  // section's checkbox state reflects the URL (and `totalActiveFilters`
  // counts every refinement, not just brand/category).
  const dynamicFacetSelections = useMemo(() => {
    const url = new URLSearchParams(searchParams.toString());
    return Object.fromEntries(
      dynamicCategoryFilters.map((filter) => [
        filter.classifyingcategory,
        parseSelectedTerms(url, dynamicFacetAttr(filter.classifyingcategory)),
      ]),
    );
  }, [dynamicCategoryFilters, searchParams]);

  // EWM-185 / EWM-240 — drawer accordions: Brand (expanded), Category
  // (collapsed), then the dynamic per-category sections. Featured
  // dynamic sections render first and auto-expand on open (Luke 2026-
  // 06-01); non-featured render after, collapsed.
  const drawerSections: FilterDrawerSection[] = useMemo(() => {
    const sections: FilterDrawerSection[] = [];
    if (brandOptions.length > 0 || selectedBrands.length > 0) {
      sections.push({
        id: STATIC_DRAWER_FACET_ATTRIBUTES[0],
        label: translate('product.brand'),
        kind: 'multi-select',
        options: brandOptions,
        value: selectedBrands,
        defaultExpanded: true,
        // EWM-422 — Brand can list many options; cap the panel height and
        // scroll the rest rather than running the drawer long.
        scrollable: true,
      });
    }
    if (categoryOptions.length > 0 || selectedCategories.length > 0) {
      sections.push({
        id: STATIC_DRAWER_FACET_ATTRIBUTES[1],
        label: translate('product.categories-id'),
        kind: 'multi-select',
        options: categoryOptions,
        value: selectedCategories,
        // EWM-597 — Category can list many options just like Brand; cap the
        // panel height and scroll the rest so the drawer doesn't run long
        // (matches the Brand section's scroll behaviour, EWM-422).
        scrollable: true,
      });
    }
    for (const filter of dynamicCategoryFilters) {
      const facetAttr = dynamicFacetAttr(filter.classifyingcategory);
      const counts = algoliaFacets[facetAttr] ?? {};
      const options = Object.entries(counts)
        .sort(([, a], [, b]) => b - a)
        .map(([value, count]) => ({ value, label: `${value} (${count})` }));
      const selected = dynamicFacetSelections[filter.classifyingcategory] ?? [];
      // Skip empty sections so the drawer doesn't bloat with no-result
      // accordions on categories whose data hasn't been aligned yet
      // (Leo's "swapped" non-meter categories).
      if (options.length === 0 && selected.length === 0) continue;
      sections.push({
        id: facetAttr,
        label: formatFilterLabel(filter.filterattribute),
        kind: 'multi-select',
        options,
        value: selected,
        defaultExpanded: !!filter.isFeatured,
      });
    }
    return sections;
  }, [
    brandOptions,
    selectedBrands,
    categoryOptions,
    selectedCategories,
    translate,
    dynamicCategoryFilters,
    algoliaFacets,
    dynamicFacetSelections,
  ]);

  // EWM-686 — the active quick-filter checkboxes count toward the "All Filters"
  // badge too, otherwise ticking "Show in-stock only" narrows the results while
  // the badge still reads 0.
  const activeQuickFilterCount = quickFilterPills.filter((pill) => pill.active).length;

  // EWM-1074 review B2 — `?barcode=` is a REFINEMENT that nothing counted, so a
  // barcode matching no product fell through `resolveListingState`'s
  // `activeFilterCount > 0` branch and reported `empty-category`: "this category
  // has no products" for a category full of them. That is the exact symptom this
  // ticket exists to remove, reached on a different channel from `facets[`.
  //
  // Counted through `normalizeBarcode` rather than off the raw param, so the
  // count and the filter agree by construction: `?barcode=-` and `?barcode=%20`
  // normalise to empty and apply NO filter (`resolve-listing-data.ts`), so they
  // must not count either. A count that could disagree with the filter is the
  // same defect class one layer up.
  const activeStandaloneCount = STANDALONE_FILTER_PARAMS.filter((param) =>
    param === 'barcode' ? normalizeBarcode(searchParams.get(param) ?? '') : searchParams.get(param),
  ).length;

  const totalActiveFilters =
    selectedBrands.length +
    selectedCategories.length +
    activeQuickFilterCount +
    activeStandaloneCount +
    Object.values(dynamicFacetSelections).reduce((sum, sel) => sum + sel.length, 0);

  const role: ProductTileV2Role = useMemo(() => {
    if (!account) return 'guest';
    if (permissions.UpdateMyCarts) return 'master';
    return 'view-only';
  }, [account, permissions.UpdateMyCarts]);

  // EWM-243 — analytics user type + active-filters summary for the GA4
  // dataLayer payloads. user_type maps the FE role taxonomy onto the
  // analytics one ('master' → 'authed'; 'view-only' → 'view_only').
  // accountId is sent raw (parity with the existing Algolia Insights
  // setAuthenticatedUserToken); see lib/analytics/dataLayer.ts comments.
  const analyticsUserType: SearchUserType = role === 'master' ? 'authed' : role === 'view-only' ? 'view_only' : 'guest';
  const analyticsAccountId: string | undefined = role === 'guest' ? undefined : account?.accountId;
  const analyticsFilters: SearchFilters = useMemo(() => {
    const f: SearchFilters = {};
    if (selectedBrands.length > 0) f.brand = selectedBrands;
    if (selectedCategories.length > 0) f[CATEGORY_FACET_ATTR] = selectedCategories;
    quickFilterPills.forEach((p) => {
      if (p.active) f[p.id] = true;
    });
    return f;
  }, [selectedBrands, selectedCategories, quickFilterPills]);

  // EWM-243 — fire GA4 `search_results_viewed` (+ `search_no_results`
  // when the result set is empty) every time a /search render lands with
  // a query. Deps cover query/filters/sort/count so re-firing happens on
  // any user-facing change to the search context (per the AC: "Given a
  // search is submitted" — re-submissions and refinements both qualify).
  // analyticsFiltersKey is the stable identity for `analyticsFilters`;
  // the object reference changes per render but the JSON string is
  // stable across reruns with the same filter values.
  const analyticsFiltersKey = useMemo(() => JSON.stringify(analyticsFilters), [analyticsFilters]);
  useEffect(() => {
    if (!isSearchRoute) return;
    if (!searchQuery) return;
    const isZero = nbHits === 0;
    pushSearchResultsViewed({
      query: searchQuery,
      filters: analyticsFilters,
      sort: currentSort,
      results_count: nbHits,
      is_zero_results: isZero,
      user_type: analyticsUserType,
      customer_account_id: analyticsAccountId,
    });
    if (isZero) {
      pushSearchNoResults({
        query: searchQuery,
        filters: analyticsFilters,
        user_type: analyticsUserType,
        customer_account_id: analyticsAccountId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchRoute, searchQuery, nbHits, currentSort, analyticsFiltersKey, analyticsUserType, analyticsAccountId]);

  // Page state. With server-only data fetching:
  //   - `error`: server tastic didn't produce `initialAlgoliaData` at all.
  //              `algoliaErrorType` discriminates network (5xx/timeout)
  //              vs validation (400) for the page-level copy.
  //   - `ready`: hits arrived (prices may still be streaming in via Suspense)
  //   - `no-matches-query`: search returned zero hits AND no filters are
  //                         active — distinct from `no-matches` so the page
  //                         renders the EWM-242 "No results for {q}" UX
  //                         (Browse all products CTA) instead of the
  //                         filtered-zero "Clear Filters" CTA.
  //   - `no-matches`: zero hits while filters are active (any context)
  //   - `empty-category`: category browse with zero hits, no filters — ALSO the
  //                       state for a real category the dealer's product
  //                       selection excludes (EWM-855)
  // Branch order is load-bearing and lives in `helpers/resolveListingState` so it
  // can be tested without mounting this component — see that file's note.
  const state: ProductListingState = useMemo(
    () =>
      resolveListingState({
        noProductSelection,
        categoryOutOfSelection,
        hasAlgoliaData: Boolean(initialAlgoliaData),
        hitCount: hits.length,
        isSearchPage,
        // EWM-686 — every refinement counts, not just brands.
        activeFilterCount: totalActiveFilters,
      }),
    [noProductSelection, categoryOutOfSelection, initialAlgoliaData, hits.length, isSearchPage, totalActiveFilters],
  );

  // EWM-238: /search uses the SearchPageHeader (H1 + input) instead of a
  // breadcrumb + category title. Leave breadcrumb undefined on the search
  // route so the page-level component doesn't render the legacy header.
  // EWM-538 / EWM-184: breadcrumb reads "Home > {ancestor names} >
  // {Category Name}". Labels come from the CT category NAME via the shared
  // buildCategoryCrumbs util (no trailing id; same pattern as the PDP,
  // EWM-539); hrefs are the slug path. Falls back to a cleaned slug segment
  // only when a CT name is unconfigured.
  const breadcrumb =
    isSearchRoute || isCollection
      ? undefined
      : buildPlpBreadcrumb({
          slugSegments: slugArr,
          category,
          homeLabel: translate('common.home'),
        });

  // EWM-238: page shell on /search. The handler builds a fresh URLSearchParams
  // off the current URL so existing filters / sort survive the new query;
  // `page` is dropped because pagination resets on query change. `router.push`
  // (not replace) so the browser back button returns to the prior query.
  const handleQuerySubmit = useCallback(
    (nextQuery: string) => {
      const next = new URLSearchParams(searchParams.toString());
      if (nextQuery) next.set('query', nextQuery);
      else next.delete('query');
      next.delete('page');
      const qs = next.toString();
      router.push(qs ? `/search?${qs}` : '/search');
    },
    [router, searchParams],
  );

  const searchHeader: SearchPageHeaderProps | undefined = isSearchRoute
    ? {
        query: searchQuery ?? '',
        onQuerySubmit: handleQuerySubmit,
        // EWM-242: focus the input automatically when there's no query
        // yet so a user landing on /search can start typing immediately.
        // Re-focus only on first paint of the empty state; once a query
        // is set we don't steal focus from the user's interaction below.
        autoFocus: isSearchRoute && !searchQuery,
      }
    : undefined;

  const purchaseLists = useMemo(() => {
    const wishlists = Array.isArray(rawWishlists) ? rawWishlists : (rawWishlists?.items ?? []);
    return wishlists.map((w) => {
      const m = mapPurchaseList(w);
      return { id: m.id, name: m.name, itemCount: m.items.length };
    });
  }, [rawWishlists]);

  // Insights click attribution — fired synchronously before navigation.
  // queryID is server-issued per-user (see lib/algolia/server.ts) so a
  // click event references the user's own search; cached across all the
  // user's renders within a 60s window.
  const handleProductClick = useCallback(
    (productId: string, position: number) => {
      // Algolia Insights — requires the server-issued queryID for attribution.
      if (queryID) {
        trackClick({
          index: algoliaIndexName,
          queryID,
          objectIDs: [productId],
          positions: [position],
          eventName: isSearchPage ? 'Hit clicked (search)' : 'Hit clicked (PLP)',
        });
        // EWM-809 — remember the queryID so a later PDP conversion/purchase attributes
        // back to this search. objectID === sku in the product index.
        rememberQueryId(productId, queryID, algoliaIndexName);
      }
      // EWM-243 — paired GA4 `search_result_click` on /search. Fires
      // even if queryID is missing (GA4 attribution doesn't depend on
      // Algolia's per-search id the way Insights does).
      if (isSearchRoute) {
        const hit = hits.find((h) => h.objectID === productId);
        pushSearchResultClick({
          query: searchQuery ?? '',
          user_type: analyticsUserType,
          customer_account_id: analyticsAccountId,
          result_position: position,
          sku: hit?.sku ?? productId,
          product_name: hit?.name ?? '',
          objectID: productId,
        });
      }
      // GA4 select_item — all surfaces (not just search)
      const ga4Hit = hits.find((h) => h.objectID === productId);
      if (ga4Hit) {
        pushSelectItem({
          item_list_id: listId,
          item_list_name: listName,
          item: algoliaHitToGA4Item(ga4Hit, position, listId, listName),
        });
      }
    },
    [
      queryID,
      algoliaIndexName,
      isSearchPage,
      isSearchRoute,
      hits,
      searchQuery,
      analyticsUserType,
      analyticsAccountId,
      listId,
      listName,
    ],
  );

  const handleAddToCart = useCallback(
    async (productId: string, qty: number) => {
      // `productId` here is the entry id from `mapAlgoliaProduct`, which
      // mirrors `hit.objectID`. Find the source hit, map it to a Product
      // for the cart + overlay. We rebuild the entry on demand here
      // (rather than hoist `productEntries` into V2View) so the heavy
      // mapping stays inside the Suspense-resolved branch.
      const hit = hits.find((h) => h.objectID === productId);
      const sku = hit?.sku;
      if (!sku) return;
      // EWM-809 — persist the originating queryID + index on the line item so a
      // search/PLP quick-add attributes at purchase time (not just at convert).
      // objectID === sku, so no store round-trip is needed here — both values are
      // already in scope for the trackConvert call below.
      const res = await addItem([
        { sku, count: qty, ...(queryID ? { algoliaQueryId: queryID, algoliaIndex: algoliaIndexName } : {}) },
      ]);
      if (!res.success) {
        toast.error(res.message || translate('common.something-went-wrong'), { position: 'top-right' });
        return;
      }
      // EWM-434 — map the added product once (reused for the GA4 payload + the
      // Algolia objectData currency fallback below).
      const addedProduct = mapAlgoliaProduct(hit).item;
      // EWM-980 — objectData (price + currency) from the resolved cart line. CT
      // line `price.value` is NET / ex-GST (Electus stores trade prices
      // GST-exclusive, includedInPrice=false — GST is charged on top), i.e. the
      // net ex-tax price the AC asks for, NOT the RRP/list price on the Algolia
      // record.
      const cart = 'lineItems' in res ? res : undefined;
      const unitNetCents = cart?.lineItems?.find((li) => li.variant?.sku === sku)?.price?.centAmount;
      const currency = cart?.sum?.currencyCode ?? addedProduct.currency;
      if (queryID) {
        // EWM-978 — fire a click event alongside the add-to-cart event so the
        // add-to-cart is preceded by a click in Algolia's funnel.
        const position = hits.findIndex((h) => h.objectID === productId) + 1;
        trackClick({
          index: algoliaIndexName,
          queryID,
          objectIDs: [productId],
          positions: [position],
          eventName: isSearchPage ? 'Add to cart click (search)' : 'Add to cart click (PLP)',
        });
        trackConvert({
          index: algoliaIndexName,
          queryID,
          objectIDs: [productId],
          eventName: isSearchPage ? 'Add to cart (search)' : 'Add to cart (PLP)',
          ...(unitNetCents != null
            ? { objectData: [{ price: unitNetCents / 100, quantity: qty }], ...(currency ? { currency } : {}) }
            : {}),
        });
      }
      // EWM-243 — paired GA4 `search_result_add_to_cart` on /search.
      // Position is the 1-based index in the loaded result set.
      if (isSearchRoute) {
        const position = hits.findIndex((h) => h.objectID === productId) + 1;
        pushSearchResultAddToCart({
          query: searchQuery ?? '',
          user_type: analyticsUserType,
          customer_account_id: analyticsAccountId,
          sku,
          result_position: position,
          quantity: qty,
        });
      }
      // EWM-434 — toast on add-to-cart instead of the (removed) mini-cart drawer.
      toast.success(translate('cart.item-added'), { position: 'top-right' });
      pushAddToCart({
        currency: addedProduct.currency,
        items: [
          {
            item_id: sku,
            item_name: addedProduct.name,
            price: addedProduct.price,
            quantity: qty,
            item_brand: addedProduct.brand,
          },
        ],
      });
    },
    [
      hits,
      addItem,
      queryID,
      algoliaIndexName,
      isSearchPage,
      isSearchRoute,
      searchQuery,
      analyticsUserType,
      analyticsAccountId,
      translate,
    ],
  );

  const handleAddToPurchaseList = useCallback(
    async (productId: string, selectedListIds: string[]) => {
      const hit = hits.find((h) => h.objectID === productId);
      const sku = hit?.sku;
      if (!sku || selectedListIds.length === 0) return;
      // EWM-188: add at qty 0 + confirmation toast, gated on the result so
      // a failed add doesn't show a success toast. Logic + spec live in
      // ./helpers/addToPurchaseListWithFeedback (mirrors handleAddToCart).
      const success = await addToPurchaseListWithFeedback({
        addToWishlists,
        translate,
        sku,
        wishlistIds: selectedListIds,
      });
      if (!success || !hit) return;
      const addedProduct = mapAlgoliaProduct(hit).item;
      selectedListIds.forEach((listId) => {
        const list = purchaseLists.find((l) => l.id === listId);
        pushAddToList({
          item_list_id: listId,
          item_list_name: list?.name ?? listId,
          item: {
            item_id: sku,
            item_name: addedProduct.name,
            price: addedProduct.price,
            item_brand: addedProduct.brand,
          },
          quantity: 1,
          currency: addedProduct.currency === 'NZD' ? 'NZD' : 'AUD',
        });
      });
    },
    [hits, addToWishlists, translate, purchaseLists],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [setPage],
  );

  // EWM-239 / EWM-242: retry re-runs the server tastic for the current
  // URL state, which re-issues the Algolia request. Filter and sort
  // state are preserved because the URL is unchanged.
  const handleRetry = useCallback(() => {
    router.refresh();
  }, [router]);

  // EWM-243 — DRY helper for the GA4 `search_refined` event. Every
  // mutator (pill / drawer / sort / clear) routes through here so the
  // event shape stays consistent. `results_count_after` is intentionally
  // omitted at refinement time — the SSR re-run hasn't returned yet;
  // the immediately-following `search_results_viewed` event (fired by
  // the effect above when nbHits updates) carries the authoritative
  // count, so GA4 can join the two by session+timestamp.
  const pushRefined = (refinement_type: 'filter' | 'sort', refinement_value: string): void => {
    if (!isSearchRoute) return;
    pushSearchRefined({
      query: searchQuery ?? '',
      user_type: analyticsUserType,
      customer_account_id: analyticsAccountId,
      refinement_type,
      refinement_value,
    });
  };

  // EWM-977 — fire the Algolia `clickedFilters` event on facet interaction.
  // Unlike `pushRefined` (GA4, gated to /search) this fires on EVERY faceted
  // surface — PLP, search results and Algolia Collections — so Personalisation
  // learns facet affinity everywhere. `filters` are Algolia `facet:value`
  // strings; the helper no-ops on an empty set (e.g. a full deselect).
  const fireClickedFilters = (filters: string[]): void => {
    trackClickedFilters({
      index: algoliaIndexName,
      filters,
      eventName: isSearchRoute
        ? 'Filter clicked (search)'
        : isCollection
          ? 'Filter clicked (collection)'
          : 'Filter clicked (PLP)',
    });
  };

  // Common rendering props passed to both Suspense branches.
  const shellProps: V2RenderProps = {
    // EWM-238: title is no-op on /search because SearchPageHeader renders
    // its own H1 "Search". Empty string keeps the prop required-typed.
    // EWM-660: on a collection page the H1 is the collection title.
    title: collection?.title ?? (isSearchRoute ? '' : category?.name) ?? '',
    // EWM-312: short category description rendered as the H1 sub-line.
    // Suppressed on /search (SearchPageHeader owns its own header block) and on
    // collection pages (no category description).
    description: isSearchRoute || isCollection ? undefined : category?.description,
    // EWM-189: the category's editorial content (the page hides the band
    // when undefined). No separate title field — the rich-text body
    // carries its own H2/H3 headings (page H1 stays the category title).
    // Suppressed on /search and collection pages (no category).
    seoHtml: isSearchRoute || isCollection ? undefined : category?.seoContent,
    breadcrumb,
    subcategories,
    searchHeader,
    role,
    canAddToPurchaseList: canUsePurchaseLists,
    // EWM-360 — surface the dealer's BU back-order flag to every tile.
    backOrdersAllowed: selectedBusinessUnit?.backOrdersAllowed ?? false,
    state,
    errorType: algoliaErrorType,
    query: queryBlocked ? undefined : (searchQuery ?? undefined),
    totalResults: nbHits,
    quickFilterPills,
    filterPills,
    drawerSections,
    totalActiveFilters,
    currentPage,
    // EWM-379 / EWM-380: /search now uses numbered pagination (?page=N, fixed
    // 20/page — EWM-386) like the category PLP — the old Load More grew `limit` past
    // commercetools' 100-item Product Search cap and 404'd on the 4th click.
    totalPages: Math.max(1, nbPages),
    isRefining,
    purchaseLists,
    // EWM-186: sort options per the design source-of-truth — Recommended
    // (default, relevance index), Price L→H, Price H→L. "Newest" is in the
    // AC but needs a date-sorted Algolia replica that doesn't exist yet
    // (sort.ts only has name/price replicas) — deferred to a BE follow-up.
    // Name A-Z/Z-A are intentionally dropped (not in the AC/design); the
    // `name-*` replica mapping stays in sort.ts so any bookmarked
    // name-sort URL still resolves without a crash.
    sortOptions: [
      { value: 'recommended', label: translate('product.recommended') },
      { value: 'price-asc', label: translate('product.price-low-high') },
      { value: 'price-desc', label: translate('product.price-high-low') },
      // EWM-255 — Newest sort. Maps via `SORT_TO_REPLICA_SUFFIX`
      // (`lib/algolia/sort.ts`) to the `*_createdat_desc` virtual
      // replica. Ordering is newest-first: measured 2026-09-17, that
      // replica is custom-ranked `desc(createdAt)` on prod and
      // `desc(createdAt), desc(isAvailable)` on dev, and the
      // `isAvailable` tie-breaker is inert anyway (EWM-686 — it is a
      // lifecycle alias, constant `true` across everything a shopper can
      // see). Full detail on the drift in `lib/algolia/sort.ts`.
      { value: 'newest', label: translate('product.newest') },
    ],
    sortValue: currentSort,
    viewMode: currentView,
    onPillChange: (id, value) => {
      // EWM-372: refine inside a transition so `isRefining` drives the grid
      // loading state while the SSR result streams back.
      startRefine(() => setTermFacet(id, value));
      pushRefined('filter', `${id}=${value.join(',')}`);
      // EWM-977 — clickedFilters only for NEWLY selected values. `value` is the
      // full post-change set, so a partial deselect would otherwise report the
      // remaining values as clicks; diff against the current URL terms to emit
      // just the additions (mirrors the quick-toggle `if (next)` guard).
      const prevTerms = parseSelectedTerms(new URLSearchParams(searchParams.toString()), id);
      fireClickedFilters(value.filter((v) => !prevTerms.includes(v)).map((v) => `${id}:${v}`));
    },
    onQuickFilterToggle: (id, next) => {
      const binding = [...QUICK_PILL_BINDINGS, ...SEARCH_QUICK_PILL_BINDINGS].find((b) => b.id === id);
      if (!binding) return;
      // EWM-243: fire the dataLayer event once per click, before the URL
      // push, so the event order is deterministic relative to the SSR
      // re-render that produces the new results_count.
      pushRefined('filter', `${binding.id}=${next}`);
      if (binding.kind === 'boolean') {
        startRefine(() => setBooleanFacet(binding.attribute, next));
        // EWM-977 — clickedFilters only when applying the boolean facet.
        if (next) fireClickedFilters([`${binding.attribute}:true`]);
        return;
      }
      if (binding.kind === 'url-flag') {
        // EWM-240: plain URL param toggle (e.g. `?showDiscontinued=T`).
        // Set when active, remove when inactive — the tastic reads the
        // param directly and threads it through to the Algolia call.
        startRefine(() => setUrlFlag(binding.param, next ? binding.value : null));
        return;
      }
      // Term: merge or remove this specific value from the facet's term
      // list. Multiple pills can share the same attribute; values within
      // the same attribute OR together server-side (per Algolia
      // buildFilters), which is the desired UX.
      const current = parseSelectedTerms(new URLSearchParams(searchParams.toString()), binding.attribute);
      const nextValues = next
        ? current.includes(binding.value)
          ? current
          : [...current, binding.value]
        : current.filter((v) => v !== binding.value);
      startRefine(() => setTermFacet(binding.attribute, nextValues));
      // EWM-977 — clickedFilters only when applying this term value.
      if (next) fireClickedFilters([`${binding.attribute}:${binding.value}`]);
    },
    // EWM-374: the drawer stages selections locally and commits them all here
    // on "Apply Filters" — one batched URL push instead of one per tick.
    onApplyFilters: (staged) => {
      startRefine(() => applyFacets(staged));
      const summary = Object.entries(staged)
        .filter(([, v]) => (Array.isArray(v) ? v.length > 0 : v === true))
        .map(([id, v]) => `${id}=${Array.isArray(v) ? v.join(',') : v}`)
        .join(';');
      pushRefined('filter', `apply:${summary}`);
      // EWM-977 — one clickedFilters carrying the full applied facet set.
      fireClickedFilters(
        Object.entries(staged).flatMap(([id, v]) =>
          Array.isArray(v) ? v.map((x) => `${id}:${x}`) : v === true ? [`${id}:true`] : [],
        ),
      );
    },
    onClearFilters: () => {
      startRefine(() => clearAllFacets());
      pushRefined('filter', 'clear-all');
    },
    onPageChange: handlePageChange,
    onSortChange: (value) => {
      setSort(value as typeof currentSort);
      pushRefined('sort', value);
    },
    onViewModeChange: setView,
    onProductClick: handleProductClick,
    onTileVisible: observeTile,
    onAddToCart: handleAddToCart,
    onAddToPurchaseList: handleAddToPurchaseList,
    // EWM-601 — create a new purchase list inline from the drawer, then return
    // it (shape the drawer expects) so it shows + auto-selects. New lists start
    // empty, so itemCount is 0.
    onCreatePurchaseList: async (name) => {
      const created = await createPurchaseList({ name, store: selectedStore });
      return created?.wishlistId ? { id: created.wishlistId, name: created.name ?? name, itemCount: 0 } : null;
    },
    onRetry: handleRetry,
  };

  // Hits flow straight to the renderer unchanged — no enrichHit here.
  // Each tile (rendered via `ProductTileWithCt` inside `PlpRenderer`)
  // awaits its own `ctPromisesBySku[sku]` promise under a per-tile
  // Suspense boundary, so prices stream in independently.
  return <PlpRenderer hits={hits} ctPromisesBySku={ctPromisesBySku} {...shellProps} />;
};

const ProductListViewModel = (props: Shared) => {
  return props.displayIntermediaryPage ? <IntermediaryView {...props} /> : <V2View {...props} />;
};

export default ProductListViewModel;
